import Link from "next/link";
import Image from "next/image";
import type { Metadata } from "next";
import { HelpCircle, Building2, Wrench, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { createClient } from "@/lib/supabase/server";

export const metadata: Metadata = {
  title: "FAQ — RateRight",
  description:
    "Frequently asked questions about RateRight, the simplest way to hire construction workers in Australia.",
};

// Revalidate every 5 minutes so FAQ edits show up without redeploy
export const revalidate = 300;

interface FaqEntry {
  id: string;
  category: string;
  question: string;
  answer: string;
  sort_order: number;
}

async function getFaqs(): Promise<Record<string, FaqEntry[]>> {
  try {
    const supabase = await createClient();
    const { data, error } = await supabase
      .from("faq_entries")
      .select("id, category, question, answer, sort_order")
      .eq("published", true)
      .order("sort_order", { ascending: true });

    if (error || !data) {
      return { worker: [], contractor: [], general: [] };
    }

    const grouped: Record<string, FaqEntry[]> = { worker: [], contractor: [], general: [] };
    for (const entry of data) {
      if (grouped[entry.category]) {
        grouped[entry.category].push(entry);
      }
    }
    return grouped;
  } catch {
    return { worker: [], contractor: [], general: [] };
  }
}

function FAQSection({
  title,
  icon,
  faqs,
  idPrefix,
}: {
  title: string;
  icon: React.ReactNode;
  faqs: FaqEntry[];
  idPrefix: string;
}) {
  if (faqs.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary">
          {icon}
        </div>
        <h2 className="text-xl font-bold tracking-tight">{title}</h2>
      </div>
      <Accordion type="single" collapsible className="w-full">
        {faqs.map((faq, i) => (
          <AccordionItem key={faq.id} value={`${idPrefix}-${i}`}>
            <AccordionTrigger>{faq.question}</AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export default async function FAQPage() {
  const faqs = await getFaqs();

  return (
    <div className="min-h-screen bg-background">
      {/* Orange accent bar */}
      <div className="h-1 bg-brand-orange" />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-screen-xl items-center justify-between px-4">
          <Link href="/" className="flex items-center gap-2 min-h-[44px] py-2">
            <Image src="/logo_black.svg" alt="RateRight" width={140} height={40} className="h-8 w-auto" priority />
          </Link>
          <Link href="/auth/login">
            <Button variant="ghost" size="default" className="min-h-[44px]">
              Sign in
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-screen-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <HelpCircle className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="mt-2 text-muted-foreground">
            Everything you need to know about hiring and finding work on
            RateRight.
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="px-4 pb-16">
        <div className="mx-auto max-w-screen-sm space-y-10">
          <FAQSection
            title="For Workers"
            icon={<Wrench className="h-4 w-4 text-primary-foreground" />}
            faqs={faqs.worker}
            idPrefix="worker"
          />

          <FAQSection
            title="For Contractors"
            icon={<Building2 className="h-4 w-4 text-primary-foreground" />}
            faqs={faqs.contractor}
            idPrefix="contractor"
          />

          <FAQSection
            title="General Questions"
            icon={<Users className="h-4 w-4 text-primary-foreground" />}
            faqs={faqs.general}
            idPrefix="general"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="border-t px-4 py-12">
        <div className="mx-auto max-w-screen-sm text-center">
          <h2 className="text-xl font-bold tracking-tight">
            Still have questions?
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Get in touch and we&apos;ll get back to you as soon as we can.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link href="/auth/signup" className="w-full min-h-[44px] sm:w-auto">
              <Button className="w-full sm:w-auto h-12 gap-2 text-base font-semibold">
                Get started
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t px-4 py-8">
        <div className="mx-auto max-w-screen-sm text-center text-sm text-muted-foreground">
          <div className="flex items-center justify-center gap-2">
            <Image src="/logo_black.svg" alt="RateRight" width={110} height={32} className="h-6 w-auto" />
          </div>
          <p className="mt-2">
            The simplest way to hire construction workers in Australia.
          </p>
          <p className="mt-4">
            &copy; {new Date().getFullYear()} RateRight Pty Ltd. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
